import { ComponentProps, useCallback } from 'react';
import { IModalDescription, IModalManager, IModalProps, LayoutConfig, ModalIdentifier } from './manager.interface';
import { useModal } from './useModal';
import { CANCELLED_BY_USER } from './errors';

export type ModalHandle<T> = { promise: Promise<T>; close: () => void; uniqId: ModalIdentifier };

export const __experimental_promisifyModalHandle =
  <
    M extends IModalDescription<P>,
    P extends IModalProps<R> = ComponentProps<Awaited<ReturnType<M['loader']>>>,
    R = Parameters<P['onSuccess']>[0],
  >(
    modalDescription: M,
    manager: IModalManager,
  ) =>
  async (
    props: Omit<P, 'onSuccess' | 'onCancel' | 'onFailure'>,
    parent?: ModalIdentifier,
    layout?: LayoutConfig,
  ): Promise<ModalHandle<R>> => {
    let resolve, reject;
    const promise = new Promise<R>((onfulfill, onerror) => {
      resolve = onfulfill;
      reject = onerror;
    });
    const uniqId = await manager.open<M, P>(
      modalDescription,
      {
        ...(props as P),
        onCancel: () => reject(CANCELLED_BY_USER),
        onFailure: (err) => reject(err),
        onSuccess: (v) => resolve(v),
      },
      parent,
      layout,
    );
    return { promise, uniqId, close: () => manager.close(uniqId) };
  };

export const useModalHandler = <
  M extends IModalDescription<P>,
  P extends IModalProps<R> = ComponentProps<Awaited<ReturnType<M['loader']>>>,
  R = Parameters<P['onSuccess']>[0],
>(
  modalDescription: M,
) => {
  const { openModal, closeModal } = useModal();

  return useCallback(
    async (
      props: Omit<P, 'onFailure' | 'onSuccess' | 'onCancel'>,
      parent?: ModalIdentifier,
      layout?: LayoutConfig,
    ): Promise<ModalHandle<R>> => {
      let resolve, reject;
      const promise = new Promise<R>((onfulfill, onerror) => {
        resolve = onfulfill;
        reject = onerror;
      });
      const uniqId = await openModal<M, P>(
        modalDescription,
        {
          ...(props as P),
          onCancel: () => reject(CANCELLED_BY_USER),
          onFailure: (err) => reject(err),
          onSuccess: (v) => resolve(v),
        },
        parent,
        layout,
      );
      return { promise, uniqId, close: () => closeModal(uniqId) };
    },
    [openModal, closeModal, modalDescription],
  );
};

export const useModalPromise = <
  M extends IModalDescription<P>,
  P extends IModalProps<R> = ComponentProps<Awaited<ReturnType<M['loader']>>>,
  R = Parameters<P['onSuccess']>[0],
>(
  modalDesccription: M,
) => {
  const handler = useModalHandler<M, P>(modalDesccription);
  return useCallback(
    async (...args: Parameters<typeof handler>) => {
      const { promise } = await handler(...args);
      return promise;
    },
    [handler],
  );
};
